import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Divider,
  Grid,
  List,
  ListItem,
  SelectChangeEvent,
  Snackbar,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import styled from '@emotion/styled';
import { useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { ReactComponent as User } from './user.svg';
import { ReactComponent as Car } from './car.svg';
import { ReactComponent as UserCar } from './user-car.svg';
import { setCurrentStage, setSelectedStage } from '../../redux/slices/vehicle';
import { useAppSelector } from '../../hooks/useRedux';
import ProductionModal from './ProductionModal';
import { containmentService, lifeCycleSnapshot, vehicleService } from '../../services/vehicles';
import { getDeliveryDetail, getRegistrationDetail } from '../../services/registration';
import { customDate } from '../../utils/date';
import ReleaseAlert from './ReleaseAlert';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }: any) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
  },
}));

// prioritizing every stage for steps
export const stagePriority: any = {
  Reserved: 1,
  Ordered: 2,
  Production: 3,
  Transit: 4,
  Inspection: 5,
  'Vehicle Ready': 6,
  Delivery: 7,
  CustomerHandoff: 8,
  VehicleRegistration: 9,
};

// prioritizing every customer stage for steps
export const customerPriority: any = {
  Reservation: 0,
  Reserved: 0,
  Ordered: 0,
  'Information Collection': 1,
  'Information Finalization': 1,
  'Signature & Payment': 2,
  'Signature & Payment ': 3,
  'Customer Ready': 4,
  'Scheduled Delivery': 4,
  Handoff: 5,
  Ownership: 6,
};
interface Props {
  transactionErrors?: any;
  setRetryDecisionPending?: any;
  setTransactionErrors?: any;
}

const VehicleStageGraph: React.FC<Props> = ({ transactionErrors, setRetryDecisionPending, setTransactionErrors }) => {
  // use hooks
  const dispatch = useDispatch();
  const { reservationId } = useParams<{ reservationId: string }>();

  // refs
  const wrapper = useRef<any>(null);

  // global states
  const { currentStage, data, quickGlanceData } = useAppSelector((state) => state.vehicle);
  // const reservationDate = useAppSelector((state) => state.vehicle.data.reservationDate);
  // const stageUpdatedDate = useAppSelector((state) => state.vehicle.data.stageUpdatedDate);

  // local states
  const [flag, setFlag] = useState('');
  const [show, setShow] = useState<boolean>(false);
  const [width, setWidth] = useState(0);
  const [currentStep, setCurrentStep] = useState(currentStage);
  const [showToast, setShowToast] = useState(false);
  const [lifecycleData, setLifecycleData] = useState<any>([]);
  const [containment, setContainment] = useState<any>([]);
  const [showUnLicenseWarning, setShowUnLicenseWarning] = useState<boolean>(false);
  const [showUnLicensePaymentWarning, setShowUnLicensePaymentWarning] = useState<boolean>(false);
  const [inspectionDate, setInspectionDate] = useState(null);
  // const [paymentStatus, setPaymentStatus] = useState<any>(null);

  function percentage(num: number, per: number) {
    return (num / 100) * per;
  }

  // constants
  const branchWidth = percentage(width, 45) < 500 ? 500 : percentage(width, 45);
  const initialStepWidth = percentage(width, 10) < 100 ? 100 : percentage(width, 10);
  const finalStepWidth = percentage(width, 23) < 250 ? 250 : percentage(width, 23);
  const userIconHeight = 28;
  const carIconHeight = 8;
  const curveHeight = 50;
  const curveWidth = 100;
  const carSteps = ['Production', 'Transit', 'Inspection', 'VehicleReady', 'Vehicle Ready'];
  const userSteps = [
    'Reserved',
    'Ordered',
    'Information Collection',
    'Information Finalization',
    'Signature & Payment',
    'Signature & Payment ',
    'Customer Ready',
    'Scheduled Delivery',
  ];
  const finalSteps = ['Handoff', 'Ownership', 'Delivery', 'CustomerHandoff', 'VehicleRegistration'];

  // all steps
  const allSteps = [
    'Reserved',
    'Ordered',
    'Production',
    'Transit',
    'Inspection',
    'Registration',
    'Pricing',
    'Payments',
    'Delivery',
    'CustomerHandoff',
    'VehicleRegistration',
  ];

  // step position for car and user Icon
  const stepPositions: Record<string, SxProps> = {
    Reserved: {
      left: -5,
      top: '50%',
      transform: 'translateY(-50%)',
    },
    Cancelled: {
      left: -5,
      top: '50%',
      transform: 'translateY(-50%)',
    },
    Ordered: {
      right: -10,
      top: '50%',
      transform: 'translateY(-50%)',
    },
    Production: {
      right: -initialStepWidth,
      top: -curveHeight,
      transform: `translate(50%, -${carIconHeight / 2}px)`,
    },
    Transit: {
      right: -initialStepWidth - (1 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(50%, -${carIconHeight / 2}px)`,
    },
    Inspection: {
      right: -initialStepWidth - (2 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(50%, -${carIconHeight / 2}px)`,
    },
    'Vehicle Ready': {
      right: -initialStepWidth - 140 - (2 / 3) * branchWidth,
      top: -curveHeight,
      transform: `translate(50%, -${carIconHeight / 2}px)`,
    },
    VehicleReady: {
      right: -initialStepWidth - branchWidth,
      top: -curveHeight,
      transform: `translate(50%, -${carIconHeight / 2}px)`,
    },
    Registration: {
      right: -initialStepWidth - 5,
      top: curveHeight - 2,
      transform: `translate(0%, -${userIconHeight / 2}px)`,
    },
    Pricing: {
      right: -initialStepWidth - 20 - (1 / 3) * branchWidth,
      top: curveHeight - 2,
      transform: `translate(-60%,-${userIconHeight / 2}px)`,
    },
    Payments: {
      right: -initialStepWidth - 20 - (2 / 3) * branchWidth,
      top: curveHeight - 2,
      transform: `translate(-60%,-${userIconHeight / 2}px)`,
    },
    'Information Collection': {
      right: -initialStepWidth - 5,
      top: curveHeight - 2,
      transform: `translate(0%, -${userIconHeight / 2}px)`,
    },
    'Information Finalization': {
      right: -initialStepWidth - 5,
      top: curveHeight - 2,
      transform: `translate(0%, -${userIconHeight / 2}px)`,
    },
    'Signature & Payment': {
      right: -initialStepWidth - 20 - (1 / 3) * branchWidth,
      top: curveHeight - 2,
      transform: `translate(-60%,-${userIconHeight / 2}px)`,
    },
    'Signature & Payment ': {
      right: -initialStepWidth - 20 - (2 / 3) * branchWidth,
      top: curveHeight - 2,
      transform: `translate(-60%,-${userIconHeight / 2}px)`,
    },
    'Customer Ready': {
      right: -initialStepWidth - 160 - (2 / 3) * branchWidth,
      top: curveHeight - 2,
      transform: `translate(-60%,-${userIconHeight / 2}px)`,
    },
    'Scheduled Delivery': {
      right: -initialStepWidth - 160 - (2 / 3) * branchWidth,
      top: curveHeight - 2,
      transform: `translate(-60%,-${userIconHeight / 2}px)`,
    },
    Delivery: {
      right: -branchWidth - 2.3 * curveWidth,
      transform: `translateY(-70%)`,
    },
    CustomerHandoff: {
      right: -branchWidth - 2 * curveWidth - finalStepWidth / 2,
      transform: `translateY(-70%)`,
    },
    VehicleRegistration: {
      right: -branchWidth - 1.7 * curveWidth - finalStepWidth,
      transform: `translateY(-70%)`,
    },
    Handoff: {
      right: -branchWidth - 2.2 * curveWidth,
      top: curveHeight - 40,
      transform: `translateY(-70%)`,
    },
    Ownership: {
      right: -branchWidth - 2.1 * curveWidth,
      top: curveHeight - 40,
      transform: `translateY(-70%)`,
    },
  };
  const stepFinalPositions: Record<string, SxProps> = {
    Delivery: {
      right: -branchWidth - 2.3 * curveWidth,
      top: -curveHeight + 57,
      transform: `translateY(-70%)`,
    },
    CustomerHandoff: {
      right: -branchWidth - 2 * curveWidth - finalStepWidth / 2,
      top: -curveHeight + 57,
      transform: `translateY(-70%)`,
    },
    VehicleRegistration: {
      right: -branchWidth - 1.7 * curveWidth - finalStepWidth,
      top: -curveHeight + 57,
      transform: `translateY(-70%)`,
    },
  };

  // position for labels and dot
  const stepDotPositions: Record<string, SxProps> = {
    Reserved: {
      left: -45,
      top: curveHeight - 55,
    },
    Ordered: {
      right: -50,
      top: curveHeight - 55,
    },
    Production: {
      right: -initialStepWidth - 40,
      bottom: -curveHeight + 85,
    },
    Transit: {
      right: -initialStepWidth - (1 / 3) * branchWidth - 43,
      bottom: -curveHeight + 85,
    },
    Inspection: {
      right: -initialStepWidth - (2 / 3) * branchWidth - 43,
      bottom: -curveHeight + 85,
    },
    Registration: {
      right: -initialStepWidth - 47,
      top: curveHeight - 8,
    },
    Pricing: {
      right: -initialStepWidth - 25 - (1 / 3) * branchWidth - 23,
      top: curveHeight - 9,
    },
    Payments: {
      right: -initialStepWidth - 25 - (2 / 3) * branchWidth - 18,
      top: curveHeight - 9,
    },
    Delivery: {
      right: -branchWidth - 2 * curveWidth - 60,
      top: curveHeight - 55,
    },
    CustomerHandoff: {
      right: -branchWidth - 2 * curveWidth - finalStepWidth / 2 - 32,
      top: curveHeight - 55,
    },
    VehicleRegistration: {
      right: -branchWidth - 2 * curveWidth - finalStepWidth - 10,
      top: curveHeight - 55,
    },
  };

  // labels for every step
  const texts: Record<string, string> = {
    Reserved: 'Reservation',
    Ordered: 'Order',
    Production: 'Production',
    Transit: 'Transit',
    Inspection: 'Inspection',
    Registration: 'Delivery, Registration & DMV',
    Pricing: 'Pricing & Purchase Agreement',
    Payments: 'Payments',
    Delivery: 'Schedule Delivery',
    CustomerHandoff: 'Customer Handoff',
    VehicleRegistration: 'Vehicle Registration',
  };

  // checking if step should be clickable
  const checkStep = (step: string) => {
    return (
      stagePriority[step] <= stagePriority[currentStage] ||
      step === 'Registration' ||
      step === 'Pricing' ||
      step === 'Payments' ||
      stagePriority[step] === 7 ||
      stagePriority[step] === 8 ||
      stagePriority[step] === 9
    );
  };

  const checkingLabelPosition = (step: string) => {
    return stagePriority?.[step] === 3 || stagePriority?.[step] === 4 || stagePriority?.[step] === 5;
  };

  // checking if step should be faded
  const checkFaded = (step: string) => {
    return step === 'Registration' || step === 'Pricing' || step === 'Payments';
  };

  const checkingPayAtDelivery = (value: string) => {
    const val = value?.toLowerCase();
    if (val === 'pay at pickup') setShowUnLicensePaymentWarning(true);
    if (val === 'sign at pickup') setShowUnLicenseWarning(true);
    if (val === 'pay and sign at pickup') {
      setShowUnLicenseWarning(true);
      setShowUnLicensePaymentWarning(true);
    }
  };

  // handling text color
  const handleTextColor = (step: string) => {
    if (stagePriority[step] <= stagePriority[currentStage]) {
      if (currentStage === step) return 'black';
      return '#456BD9';
    }
    if (
      step === 'Registration' ||
      step === 'Pricing' ||
      step === 'Payments' ||
      stagePriority[step] === 7 ||
      stagePriority[step] === 8 ||
      stagePriority[step] === 9
    )
      return '#456BD9';
    return '#A1A1A1';
  };

  // Handle Flag select box
  const handleChange = (event: SelectChangeEvent) => {
    setFlag(event.target.value);
  };

  // handling dates
  const handleDate = (step: string) => {
    const dataObj = lifecycleData?.lifeCycleDateSnapshot?.stageDateInformation?.[0]?.[step];
    const keyMap: any = {
      Registration: 'infoFinalizedDate',
      Pricing: 'purchaseAgreementSignedDate',
      Delivery: 'scheduleDeliveryStr',
      CustomerHandoff: 'deliveryHandoffDate',
    };
    const customerDates = lifecycleData?.[keyMap[step]];

    if ((dataObj?.endDate && dataObj?.endDate !== '') || (customerDates && customerDates !== '')) {
      return (
        <BootstrapTooltip title={dataObj?.subStage}>
          <Typography
            fontSize={10}
            fontWeight={500}
            color="#5C5C5C"
            sx={{
              background: '#F2F2F2',
              borderRadius: '4px',
              padding: '2px 4px',
              display: 'inline-block',
              textAlign: 'center',
            }}
          >
            {step === 'Delivery'
              ? customDate(customerDates, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })
              : moment(dataObj?.endDate ?? customerDates, 'Y-MM-DD hh:mm:ss')?.format('MMM DD, Y')}
          </Typography>
        </BootstrapTooltip>
      );
    }
    return null;
  };

  // handling icons
  const handlingUserCarIcon = (stage: string) => {
    if (carSteps.includes(stage)) {
      return <Car />;
    }
    if (userSteps.includes(stage)) {
      return <User />;
    }
    return null;
  };
  const handlingFinalSteps = (stage: string, cStage: string) => {
    if (stagePriority?.[stage] > 6 && customerPriority[data?.customerStage] < 5)
      return (
        <Box position="absolute" sx={{ ...(stepFinalPositions[stage] || {}), zIndex: 4 }}>
          <Car />
        </Box>
      );

    if (customerPriority[cStage] > 4 && stagePriority?.[stage] < 7)
      return (
        <Box position="absolute" sx={{ ...(stepPositions[cStage] || {}), zIndex: 4 }}>
          <User />
        </Box>
      );

    if (customerPriority[cStage] > 4 && stagePriority?.[stage] > 6)
      return (
        <Box position="absolute" sx={{ ...(stepPositions[currentStage] || {}), zIndex: 4 }}>
          <UserCar />
        </Box>
      );

    return null;
  };

  // handle rendering dots, clock, checked
  const handleRender = (step: string) => {
    const handleColor = () => {
      if (currentStep === step) return '#456BD9';
      if (!checkFaded(step)) return '#A1A1A1';
      return '#000';
    };

    if (stagePriority[step] <= stagePriority[currentStage])
      return (
        <Icon
          style={{
            fontSize: 19,
            position: 'relative',
            top: -4,
            background: 'white',
            color: currentStep === step ? '#456BD9' : 'black',
          }}
          icon="bi:check-circle-fill"
        />
      );
    if (step === 'Registration') {
      if (customerPriority?.[data?.customerStage] >= 1) {
        return (
          <Icon
            style={{
              fontSize: 19,
              position: 'relative',
              top: -4,
              background: 'white',
              color: currentStep === step ? '#456BD9' : 'black',
            }}
            icon="bi:check-circle-fill"
          />
        );
      }
      return (
        <Box
          sx={{
            height: '19px',
            transform: 'none',
            ...(stepDotPositions[step] || {}),
            zIndex: 2,
          }}
        >
          <Box height={10} width={10} bgcolor="#fff" borderRadius="50%" border={`2px solid ${handleColor()}`} />
        </Box>
      );
    }
    if (step === 'Pricing') {
      if (
        quickGlanceData?.purchaseAgreementSignedDate &&
        quickGlanceData?.purchaseAgreementSignedDate !== null &&
        quickGlanceData?.purchaseAgreementSignedDate !== ''
      )
        return (
          <Icon
            style={{
              fontSize: 19,
              position: 'relative',
              top: -4,
              background: 'white',
              color: currentStep === step ? '#456BD9' : 'black',
            }}
            icon="bi:check-circle-fill"
          />
        );
      return (
        <Icon
          icon="ep:clock"
          style={{
            fontSize: 19,
            position: 'relative',
            top: -4,
            color: '#456BD9',
            border: `2px solid #fff`,
            background: '#fff',
          }}
        />
      );
    }
    if (step === 'Payments') {
      if (quickGlanceData?.RemainingAmountAtHandoff <= 0 || quickGlanceData?.RemainingAmountAtHandoff <= 0.0)
        return (
          <Icon
            style={{
              fontSize: 19,
              position: 'relative',
              top: -4,
              background: 'white',
              color: currentStep === step ? '#456BD9' : 'black',
            }}
            icon="bi:check-circle-fill"
          />
        );
      return (
        <Icon
          icon="ep:clock"
          style={{
            fontSize: 19,
            position: 'relative',
            top: -4,
            color: '#456BD9',
            border: `2px solid #fff`,
            background: '#fff',
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          height: '19px',
          transform: 'none',
          ...(stepDotPositions[step] || {}),
          zIndex: 2,
        }}
      >
        <Box height={10} width={10} bgcolor="#fff" borderRadius="50%" border={`2px solid ${handleColor()}`} />
      </Box>
    );
  };

  // fetching record
  const fetchReservedData = useCallback(async () => {
    try {
      const resp = await lifeCycleSnapshot({
        method: 'POST',
        url: 'dates',
        data: {
          reservationId,
          langCode: 'en-us',
        },
      });
      setLifecycleData(resp?.data?.data);
    } catch (error) {
      console.log(error);
    }
  }, [reservationId]);

  // fetching containment record
  const fetchContainmentData = useCallback(async (VIN: string) => {
    setContainment([]);
    try {
      const resp = await containmentService({
        method: 'GET',
        params: {
          VIN,
        },
      });
      setContainment(resp?.data?.data);
    } catch (error) {
      console.log(error);
    }
  }, []);

  // fetching record
  const fetchInspectionData = useCallback(async () => {
    if (stagePriority[currentStage] >= 6) {
      console.log('run check');
      try {
        const resp = await lifeCycleSnapshot({
          method: 'POST',
          data: {
            reservationId,
            stages: ['Inspection'],
            langCode: 'en-us',
          },
        });
        const stageInformation = resp?.data?.data?.[0]?.stageInformation?.find(
          (item: any) => item?.subStage === 'Inspection With Hold'
        );
        if (stageInformation?.ruleResult?.onHoldStartDate && stageInformation?.ruleResult?.onHoldStartDate !== '')
          setInspectionDate(stageInformation?.ruleResult?.onHoldStartDate);
        else setInspectionDate(null);
      } catch (error) {
        console.log(error);
      }
    }
  }, [reservationId, currentStage]);

  // lifecycle hooks
  useEffect(() => {
    if (reservationId) {
      fetchReservedData();
      getDeliveryDetail(reservationId as string).then((response) => {
        setShowUnLicenseWarning(false);
        setShowUnLicensePaymentWarning(false);
        if (response?.deliveryLocation?.paySignAtPickUp)
          checkingPayAtDelivery(response?.deliveryLocation?.paySignAtPickUp);
      });
      if (data?.VIN && data?.VIN !== '') fetchContainmentData(data?.VIN);
      fetchInspectionData();
    }
  }, [fetchReservedData, fetchContainmentData, data?.VIN, reservationId, fetchInspectionData]);

  useLayoutEffect(() => {
    setWidth(wrapper?.current?.offsetWidth);
    setCurrentStep(currentStage);
  }, [currentStage]);

  const temp = {
    sfdcResponse: {
      statusCode: 200,
      data: 'Hello',
    },
    vmsResponse: {
      statusCode: 400,
      data: 'Hello',
    },
  };
  return (
    <Box overflow="auto" width="100%" ref={wrapper}>
      <Grid container py={0} alignItems="center">
        <Grid item xs={5}>
          {transactionErrors && (
            <ReleaseAlert
              transactionErrors={transactionErrors}
              setRetryDecisionPending={setRetryDecisionPending}
              setTransactionErrors={setTransactionErrors}
            />
          )}
          {/* <ReleaseAlert
            transactionErrors={temp}
            setRetryDecisionPending={setRetryDecisionPending}
            setTransactionErrors={setTransactionErrors}
          /> */}
          <List sx={{ display: 'flex', margin: 0, padding: 0 }}>
            <ListItem sx={{ padding: 0, display: 'inline-flex', width: 'auto' }}>
              <Typography fontSize={16} fontWeight={500}>
                Track Delivery Readiness
              </Typography>
            </ListItem>
            <ListItem sx={{ padding: 0, display: 'inline-flex', width: 'auto' }}>
              <Divider sx={{ mx: 1, height: '14px', width: '10px' }} orientation="vertical" />
            </ListItem>

            {transactionErrors && (
              <ListItem sx={{ padding: 0, display: 'inline-flex', width: 'auto' }}>
                <Icon icon="mdi:flag" width="20px" height="20px" color="#E04732" />
                <Typography fontSize={12} fontWeight={100}>
                  Pinned
                </Typography>
              </ListItem>
            )}
            {/* <ListItem sx={{ padding: 0, display: 'inline-flex', width: 'auto' }}>
              <Select
                value={flag}
                onChange={handleChange}
                displayEmpty
                variant="standard"
                sx={{
                  '&:before': { content: 'none' },
                  '&:after': { content: 'none' },
                  '&:focus': { content: 'none' },
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#717171',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  padding: 0,
                }}
              >
                <MenuItem
                  value=""
                  sx={{
                    padding: '10px 15px',
                    display: 'flex',
                    backgroundColor: 'rgba(69, 107, 217, 0) !important',
                    '&:hover': { backgroundColor: 'rgba(69, 107, 217, 0)' },
                  }}
                >
                  <Icon icon="ci:flag-outline" fontSize={20} color="#E04732" />
                  <Typography sx={{ fontSize: 12, color: '#717171', display: 'inline-block', marginLeft: '5px' }}>
                    Unflagged
                  </Typography>
                </MenuItem>
                <MenuItem
                  value="10"
                  sx={{
                    fontSize: 12,
                    color: '#717171',
                    padding: '10px 15px',
                    display: 'flex',
                    backgroundColor: 'rgba(69, 107, 217, 0) !important',
                    '&:hover': { backgroundColor: 'rgba(69, 107, 217, 0)' },
                  }}
                >
                  <Icon icon="ci:flag-fill" fontSize={20} color="#E04732" />
                  <Typography sx={{ fontSize: 12, color: '#717171', display: 'inline-block', marginLeft: '5px' }}>
                    Pinned
                  </Typography>
                </MenuItem>
                <MenuItem
                  value="20"
                  sx={{
                    fontSize: 12,
                    color: '#717171',
                    padding: '10px 15px',
                    display: 'flex',
                    backgroundColor: 'rgba(69, 107, 217, 0) !important',
                    '&:hover': { backgroundColor: 'rgba(69, 107, 217, 0)' },
                  }}
                >
                  <Icon icon="ci:flag-fill" fontSize={20} color="#E04732" />
                  <Typography sx={{ fontSize: 12, color: '#717171', display: 'inline-block', marginLeft: '5px' }}>
                    Unresponsive
                  </Typography>
                </MenuItem>
              </Select>
            </ListItem> */}
          </List>
        </Grid>
        {/* <Grid item xs={7}>
          <Stack direction="row" gap={2} alignItems="center" justifyContent="end">
            <Typography fontSize={14} fontWeight={500}>
              Reservation to Delivery
            </Typography>
            <Typography fontSize={14} fontWeight={500}>
              Ownership
            </Typography>
          </Stack>
        </Grid> */}
      </Grid>
      <Divider sx={{ my: 1 }} />
      {/* - - Containment - - - - - - */}
      {(data?.VIN && data?.VIN !== '' && containment?.length > 0) || inspectionDate ? (
        <Box
          sx={{
            border: '1px solid #E04732',
            borderRadius: '4px',
            padding: '5px 8px',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            width: 'calc(100% - 50px)',
          }}
          my={1.5}
        >
          <Icon icon="ri:information-line" fontSize={14} color="#E04732" />
          <Typography fontSize={12} fontWeight={400} color="#E04732" marginLeft={1.2} flexGrow={1}>
            Vehicle has {inspectionDate ? (containment?.length ?? 0) + 1 : containment?.length} warning
            {(inspectionDate ? (containment?.length ?? 0) + 1 : containment?.length) > 1 ? 's' : null}
          </Typography>
          {/* <Typography fontSize={12} fontWeight={400} color="#456BD9">
            View more
          </Typography> */}
          <List sx={{ flexBasis: '100%', margin: '3px 0 0 20px', padding: '0' }}>
            {containment?.map((con: any) => {
              return (
                <ListItem key={con?._id} sx={{ padding: '0 0 0 15px' }}>
                  <Typography fontSize={12} fontWeight={400}>
                    {con?.containmentHoldReason} as of {con?.createdAt && moment(con.createdAt)?.format('MMM DD, Y')}
                  </Typography>
                </ListItem>
              );
            })}
            {inspectionDate && (
              <ListItem sx={{ padding: '0 0 0 15px' }}>
                <Typography fontSize={12} fontWeight={400}>
                  Work Order hold as of {moment(inspectionDate)?.format('MMM DD, Y')}
                </Typography>
              </ListItem>
            )}
          </List>
        </Box>
      ) : null}

      <Stack minHeight={150} minWidth={840} pt={12} pl={4} marginTop={3}>
        {/*
         *  Initial steps
         */}
        <Box
          bgcolor={stagePriority[currentStage] > 1 ? '#000' : '#C4C4C4'}
          height={4}
          width={initialStepWidth}
          position="relative"
        >
          {/**
           * Moving user icon
           */}
          {stagePriority[currentStage] > 2 && stagePriority[currentStage] < 7 && (
            <Box position="absolute" sx={{ ...(stepPositions[currentStage] || {}), zIndex: 4 }}>
              {handlingUserCarIcon(currentStage)}
            </Box>
          )}
          {customerPriority[data?.customerStage] < 1 ? (
            <Box position="absolute" sx={{ ...(stepPositions[currentStage] || {}), zIndex: 4 }}>
              {handlingUserCarIcon(currentStage)}
            </Box>
          ) : (
            <Box position="absolute" sx={{ ...(stepPositions[data?.customerStage] || {}), zIndex: 4 }}>
              {handlingUserCarIcon(data?.customerStage)}
            </Box>
          )}

          {stagePriority?.[currentStage] > 6 || customerPriority[data?.customerStage] > 4
            ? handlingFinalSteps(currentStage, data?.customerStage)
            : null}

          {/**
           * step labels - - - - - - - - -
           */}
          {allSteps.map((step) => (
            <Box
              position="absolute"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                ...(stepDotPositions[step] || {}),
                zIndex: 3,
                cursor: checkStep(step) ? 'pointer' : 'cursor',
              }}
              onClick={() => {
                if (checkStep(step)) {
                  setCurrentStep(step);
                  // dispatch(setCurrentStage(step));
                  dispatch(setSelectedStage(step));
                }
              }}
            >
              {stagePriority?.[step] === 3 || stagePriority?.[step] === 4 || stagePriority?.[step] === 5
                ? null
                : handleRender(step)}

              <Box position="relative">
                <Typography
                  textAlign="center"
                  maxWidth={115}
                  minWidth={105}
                  fontSize={14}
                  fontWeight={500}
                  color={handleTextColor(step)}
                  lineHeight={1.1}
                  marginTop={checkingLabelPosition(step) ? '0px' : '5px'}
                  marginBottom={checkingLabelPosition(step) ? '5px' : ''}
                >
                  {texts[step]}
                  {/* {step === 'Production' ? ( */}
                  {/*  <Icon icon="cil:loop" onClick={() => setShow(true)} fontSize={13} style={{ marginLeft: 4 }} /> */}
                  {/* ) : null} */}
                </Typography>
              </Box>
              <Box
                sx={{
                  marginTop: checkingLabelPosition(step) ? '0px' : '10px',
                  marginBottom: checkingLabelPosition(step) ? '10px' : '',
                }}
              >
                {handleDate(step)}
                {(!quickGlanceData?.purchaseAgreementSignedDate ||
                  quickGlanceData?.purchaseAgreementSignedDate === '') &&
                  showUnLicenseWarning &&
                  step === 'Pricing' && (
                    <Typography
                      color="#E04732"
                      fontSize={12}
                      fontWeight={500}
                      sx={{
                        minWidth: '104px',
                        padding: '2px 0',
                        lineHeight: 1,
                        border: '1px solid #E04732',
                        borderRadius: '2px',
                        display: 'inline-block',
                        textAlign: 'center',
                      }}
                    >
                      Sign at Delivery
                    </Typography>
                  )}
                {(quickGlanceData?.RemainingAmountAtHandoff > 0 || quickGlanceData?.RemainingAmountAtHandoff > 0.0) &&
                  showUnLicensePaymentWarning &&
                  step === 'Payments' && (
                    <Typography
                      color="#E04732"
                      fontSize={12}
                      fontWeight={500}
                      sx={{
                        minWidth: '104px',
                        padding: '2px 0',
                        lineHeight: 1,
                        border: '1px solid #E04732',
                        borderRadius: '2px',
                        display: 'inline-block',
                        textAlign: 'center',
                      }}
                    >
                      Pay at Delivery
                    </Typography>
                  )}
              </Box>
              {stagePriority?.[step] === 3 || stagePriority?.[step] === 4 || stagePriority?.[step] === 5
                ? handleRender(step)
                : null}
            </Box>
          ))}

          {/**
           * Vehicle stage branch start
           */}
          <Box position="absolute" right={4} top={8} sx={{ transform: 'translate(99%, -100%)' }} zIndex={2}>
            <svg width="268" height={curveHeight} viewBox="0 0 268 54" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M2 52C37.5674 52 44.1368 41.398 53.0968 27.3769C65.6205 7.77889 88.6641 2 104.194 2G200"
                stroke={stagePriority[currentStage] > 2 ? '#000' : '#C4C4C4'}
                strokeWidth="5"
                strokeLinecap="round"
              />
            </svg>
          </Box>

          {/**
           * Customer stage branch start
           */}
          <Box position="absolute" right={6} sx={{ transform: 'translate(100%, 0%)' }} zIndex={1}>
            <svg width="123" height={curveHeight} viewBox="0 0 123 54" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M2 2C37.5674 2 44.1368 12.602 53.0968 26.6231C65.6205 46.2211 88.6641 52 104.194 52H120.5"
                stroke={customerPriority[data?.customerStage] >= 1 ? '#000' : '#C4C4C4'}
                strokeWidth="5"
                strokeLinecap="round"
              />
            </svg>
          </Box>

          {/**
           * Vehicle stage branch - - - - - - - -
           */}
          <Box
            bgcolor={stagePriority[currentStage] > 3 ? '#000' : '#C4C4C4'}
            height={4}
            width={branchWidth / 2.7}
            position="absolute"
            right={-((1 / 3) * branchWidth) - 120}
            top={-curveHeight + 4}
          />
          <Box
            bgcolor={stagePriority[currentStage] > 4 ? '#000' : '#C4C4C4'}
            height={4}
            width={branchWidth / 3}
            position="absolute"
            right={-((2 / 3) * branchWidth) - 120}
            top={-curveHeight + 4}
          />
          <Box
            bgcolor={stagePriority[currentStage] >= 6 ? '#000' : '#C4C4C4'}
            height={4}
            width={branchWidth / 4.5}
            position="absolute"
            right={-branchWidth - 55}
            top={-curveHeight + 4}
          />
          <Box
            bgcolor={stagePriority[currentStage] >= 7 ? '#000' : '#C4C4C4'}
            height={4}
            width={branchWidth / 11}
            position="absolute"
            right={-branchWidth - 100}
            top={-curveHeight + 4}
          />

          {/**
           * Customer stage branch - - - - - - - - -
           */}
          <Box
            borderTop={`4px ${customerPriority[data?.customerStage] >= 2 ? 'solid #000' : 'dashed #C4C4C4'}`}
            height={0}
            width={branchWidth / 3}
            position="absolute"
            right={-((1 / 3) * branchWidth) - 100}
            top={curveHeight - 4}
          />
          <Box
            borderTop={`4px ${customerPriority[data?.customerStage] >= 3 ? 'solid #000' : 'dashed #C4C4C4'}`}
            height={0.1}
            width={branchWidth / 3}
            position="absolute"
            right={-((2 / 3) * branchWidth) - 100}
            top={curveHeight - 4}
          />
          <Box
            borderTop={`4px ${customerPriority[data?.customerStage] >= 4 ? 'solid #000' : 'dashed #C4C4C4'}`}
            height={0.1}
            width={branchWidth / 4}
            position="absolute"
            right={-branchWidth - 60}
            top={curveHeight - 4}
          />
          <Box
            borderTop={`4px solid ${customerPriority[data?.customerStage] > 4 ? '#000' : '#C4C4C4'}`}
            height={0.1}
            width={branchWidth / 10}
            position="absolute"
            right={-branchWidth - 110}
            top={curveHeight - 4}
          />

          {/**
           * Finish line - - - - - - - - Vehicle Ready - - - - Customer Ready - -
           */}
          <Box
            position="absolute"
            bgcolor="#F9B70B"
            height={124}
            width={4}
            right={-branchWidth - 55}
            sx={{ transform: 'translateY(-60px)', zIndex: 3 }}
          />
          <Typography
            sx={{
              height: '19px',
              transform: 'none',
              right: -initialStepWidth - 25 - (2 / 3) * branchWidth - 138,
              top: curveHeight - 135,
              zIndex: 2,
            }}
            fontSize={14}
            fontWeight={500}
            color="#A1A1A1"
            position="absolute"
          >
            Vehicle Ready
          </Typography>
          <Box
            position="absolute"
            bgcolor="#fff"
            border="2px solid #C4C4C4"
            height={6}
            width={6}
            borderRadius="50%"
            right={-branchWidth - 58}
            sx={{ transform: 'translateY(-50px)', zIndex: 3 }}
          />
          <Box
            position="absolute"
            bgcolor="#fff"
            border="2px solid #C4C4C4"
            height={6}
            width={6}
            borderRadius="50%"
            right={-branchWidth - 58}
            sx={{ transform: 'translateY(44px)', zIndex: 3 }}
          />
          <Typography
            sx={{
              height: '19px',
              transform: 'none',
              right: -initialStepWidth - 25 - (2 / 3) * branchWidth - 140,
              top: curveHeight + 12,
              zIndex: 2,
            }}
            fontSize={14}
            fontWeight={500}
            color="#A1A1A1"
            position="absolute"
          >
            Customer Ready
          </Typography>

          {/**
           * Vehicle stage branch end
           */}
          <Box position="absolute" right={-branchWidth - 210} sx={{ transform: 'translateY(-86%)' }} zIndex={2}>
            <svg width="110" height={curveHeight} viewBox="0 0 123 54" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M2 2C37.5674 2 44.1368 12.602 53.0968 26.6231C65.6205 46.2211 88.6641 52 104.194 52H120.5"
                stroke={stagePriority[currentStage] >= 7 ? '#000' : '#C4C4C4'}
                strokeWidth="5"
                strokeLinecap="round"
              />
            </svg>
          </Box>

          {/**
           * Customer stage branch end
           */}
          <Box position="absolute" right={-branchWidth - 360} zIndex={1}>
            <svg width="268" height={curveHeight} viewBox="0 0 268 54" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M2 52C37.5674 52 44.1368 41.398 53.0968 27.3769C65.6205 7.77889 88.6641 2 104.194 2G200"
                stroke={customerPriority[data?.customerStage] > 4 ? '#000' : '#C4C4C4'}
                strokeWidth="5"
                strokeLinecap="round"
              />
            </svg>
          </Box>

          {/*
           *  final step 1
           */}
          <Box
            bgcolor={stagePriority[currentStage] >= 8 ? '#000' : '#C4C4C4'}
            height={4}
            width={finalStepWidth / 2.5}
            position="absolute"
            right={-branchWidth - 2 * curveWidth - finalStepWidth / 2.5}
            top={0}
          />
          {/*
           *  final step 2
           */}
          <Box
            bgcolor={stagePriority[currentStage] >= 9 ? '#000' : '#C4C4C4'}
            height={4}
            width={finalStepWidth / 2.5}
            position="absolute"
            right={-branchWidth - 1.5 * curveWidth - finalStepWidth}
            top={0}
          />
        </Box>
      </Stack>
      {show && <ProductionModal show={show} onHide={() => setShow(false)} showToast={() => setShowToast(true)} />}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showToast}
        autoHideDuration={6000}
        onClose={() => setShowToast(false)}
      >
        <Alert onClose={() => setShowToast(false)} severity="success" sx={{ width: '100%' }}>
          Submitted Successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VehicleStageGraph;
